<script setup lang="ts">
import { computed, ref, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useStorage } from '@vueuse/core';
import { useDataTable, useDataTableFilters, DATA_TABLE_COLUMNS_ORDER } from '@DataTableModule';

const props = defineProps<{
  adminColumns?: string[];
  adminColumnsVisible?: string[];
  loading?: boolean;
}>();

const ROUTE = useRoute();
const { activeColumns, initialColumns, defaultVisibleColumns } = useDataTableFilters();
const { columns, frozenFields } = useDataTable();

let initialState: string[] = [];

const activeColumnsEdited = ref<boolean>(false);
const search = ref('');
const isLoading = ref<boolean>(false);
const _model = ref<boolean>(false);
const selectedOptions = ref<string[]>([]);

const storageKey = computed(() => `show-hide-columns-${String(ROUTE.name)}`);
const storedState = useStorage<boolean>(storageKey.value, false);

const frozenColumns = computed(() => [...new Set([...DATA_TABLE_COLUMNS_ORDER, ...frozenFields.value])]);
const sortedColumns = computed(() => {
  let _columns = [...columns.value];

  const matchedFields = columns.value
    .filter((column: any) => props.adminColumns?.includes(column.field))
    .map((column: any) => column.field);

  _columns = _columns.filter((column: any) => {
    if (matchedFields.includes(column.field)) {
      return props.adminColumnsVisible?.includes(column.field);
    }
    return true;
  });

  const missingFields = matchedFields.filter(
    (field: string) => props.adminColumnsVisible?.includes(field) && !_columns.some((c) => c.field === field)
  );

  missingFields.forEach((field: string) => {
    const columnToAdd = columns.value.find((c) => c.field === field);
    if (columnToAdd) {
      _columns.push(columnToAdd);
    }
  });

  const toggleableFields = _columns
    .filter(({ field }) => !frozenColumns.value.includes(field))
    .sort((a, b) => a.header.localeCompare(b.header));

  if (!search.value) return toggleableFields;

  return toggleableFields.filter(({ header }) => header.toLowerCase().includes(search.value.toLowerCase()));
});

onMounted(() => {
  if (storedState.value !== null) {
    activeColumnsEdited.value = storedState.value;
  }
});

function setEditActiveColumnsFlag() {
  const activeSet = new Set(activeColumns.value);
  const defaultSet: Set<string> = new Set();

  columns.value.forEach(({ field, visible }) => {
    if (visible) defaultSet.add(field);
  });

  activeColumnsEdited.value = activeSet.size !== defaultSet.size || ![...activeSet].every((key) => defaultSet.has(key));

  storedState.value = activeColumnsEdited.value;
}

function setNewColumns() {
  isLoading.value = true;

  activeColumns.value = initialColumns.value.filter((column: string) => selectedOptions.value.includes(column));

  resetSearch();
  setEditActiveColumnsFlag();

  isLoading.value = false;
  _model.value = false;
}

function setDefaultColumns() {
  isLoading.value = true;

  resetModel();
  resetSearch();

  storedState.value = false;
  activeColumnsEdited.value = false;

  isLoading.value = false;
}

function resetModel() {
  selectedOptions.value = defaultVisibleColumns.value;
}

function resetSearch() {
  search.value = '';
}

function getInitialState() {
  selectedOptions.value = activeColumns.value;
  initialState = selectedOptions.value;
}

function resetInitialState() {
  selectedOptions.value = initialState;
  setEditActiveColumnsFlag();
  resetSearch();
}
</script>

<template>
  <aside>
    <div class="relative">
      <Button
        v-tooltip.top="{
          value: !loading ? $t('components.dataTable.tableColumnsToggle.tooltip') : '',
          showDelay: 1000,
        }"
        class="af-filter-button h-9 w-9"
        icon="pi pi-eye"
        :disabled="loading"
        @click="_model = true"
      />

      <i
        v-if="activeColumnsEdited"
        class="pi pi-circle-fill absolute right-[-6px] top-[-4px] text-sm text-warning-500"
      />
    </div>

    <Dialog
      v-model:visible="_model"
      modal
      class="w-[650px]"
      pt:content="h-[95vh]"
      pt:header="items-start"
      @show="getInitialState"
    >
      <template #closeicon>
        <Button
          outlined
          class="border-none"
          icon="pi pi-times"
          severity="secondary"
          @click="resetInitialState"
        />
      </template>

      <template #header>
        <div class="flex flex-col gap-4">
          <h2 class="text-lg font-semibold">
            {{ $t('filters.columns.hide') }}
          </h2>

          <MSearchInput
            v-model="search"
            class="max-w-64"
            :debounceTime="0"
          />
        </div>
      </template>

      <div
        v-if="sortedColumns.length"
        class="grid grid-cols-3 gap-4"
      >
        <div
          v-for="({ header, field }, index) in sortedColumns"
          class="flex items-center gap-2 text-sm"
          :key="field"
        >
          <Checkbox
            v-model="selectedOptions"
            name="category"
            :inputId="`${field}_${index}`"
            :value="field"
          />

          <label
            class="cursor-pointer"
            :for="`${field}_${index}`"
          >
            {{ header }}
          </label>
        </div>
      </div>

      <MEmptySection
        v-else
        noAction
        class="h-full"
        icon="pi pi-filter"
        label="filters.noFiltersFound"
      />

      <template #footer>
        <div class="flex w-full justify-end gap-3 pt-5">
          <div class="flex gap-2">
            <Button
              class="af-button"
              icon="pi pi-refresh"
              severity="secondary"
              :label="$t('common.reset')"
              :disabled="isLoading"
              @click="setDefaultColumns"
            />

            <Button
              class="af-button af-button-success"
              icon="pi pi-check"
              :label="$t('common.apply')"
              :loading="isLoading"
              @click="setNewColumns"
            />
          </div>
        </div>
      </template>
    </Dialog>
  </aside>
</template>
